import { Avatar, Tag, Tooltip, Button, Space, Popconfirm } from 'antd';
import { NavLink } from 'react-router-dom';
import { EditOutlined, DeleteOutlined, CalendarOutlined, CopyOutlined } from '@ant-design/icons';
import Moment from 'react-moment';
import styles from './AffaireBacklogItem.module.scss';
import PropTypes from 'prop-types';
import { useUserContext } from '../../contexts/UserContext';
import { useSelector } from '@legendapp/state/react';
import { state$ } from '../../state/globalState';
import { affairesState } from '../../state/actions/affairesState';
import { directoriesState } from '../../state/actions/directoriesState';
import { useEffect, useState } from 'react';
import { InlineEditableInput } from '../utils/InlineEditableInput';
import { AffaireService } from '../../services/AffaireService';
import { catchError } from '../../services/DaoService';
import { copyLinkToClipboard } from '../../helpers/clipboard';
import { isAffaireLocked } from '../../helpers/permissions';
import AffaireEditForm from './AffaireEditForm';

const AffaireBacklogItem = ({ affaire, onItemClick, onUpdate }) => {
  const { check, currentUser } = useUserContext();
  const canManage = (affaire?.user_id === currentUser?.id ||
    affaire?.responsable_id === currentUser?.id) && !isAffaireLocked(affaire);
  const [editVisible, setEditVisible] = useState(false);
  const users = useSelector(() => state$.usersDirectory.list.get());

  useEffect(() => { directoriesState.loadUsers(); }, []);

  const handleFieldChange = (value, fieldName, record) => {
    AffaireService.getInstance()
      .update(record.uid, { ...record, [fieldName]: value })
      .then(r => {
        affairesState.updateAffaireInList(r.data);
        onUpdate?.(r.data);
      })
      .catch(catchError);
  };

  const priorityColor = affaire.priorite?.couleur ?? '#ccc';
  const isDone = affaire.done;

  const handleNameClick = (e) => {
    if (onItemClick) {
      e.preventDefault();
      onItemClick(affaire.uid);
    }
  };

  return (
    <div className={styles.item}>
      <div className={styles.priorityStripe} style={{ backgroundColor: priorityColor }} />

      <div className={styles.content}>
        <Tooltip title="Copier le lien du dossier">
          <div
            className={styles.codeTag}
            onClick={() => copyLinkToClipboard(`/affaire/${affaire.uid}`, 'Lien du dossier')}
            style={{ cursor: 'pointer' }}
          >
            {affaire.code} <CopyOutlined style={{ fontSize: 10 }} />
          </div>
        </Tooltip>
        <div className={styles.name}>
          <InlineEditableInput
            canEdit={canManage}
            type="text"
            fieldName="nom"
            value={affaire.nom}
            record={affaire}
            onChange={handleFieldChange}
          >
            {check('VIEW-AFFAIRE')
              ? <NavLink to={`/affaire/${affaire.uid}`} className={`${styles.nameLink} ${isDone ? styles.done : ''}`} onClick={handleNameClick}>{affaire.nom}</NavLink>
              : <span className={isDone ? styles.done : ''}>{affaire.nom}</span>
            }
          </InlineEditableInput>
        </div>
        <div className={styles.tags}>
          {affaire.modele && <Tag color={affaire.modele.couleur} style={{ fontSize: 11, border: 'none' }}>{affaire.modele.nom}</Tag>}
          {affaire.departement && <Tag style={{ fontSize: 11 }}>{affaire.departement.nom}</Tag>}
          {affaire.taches_total > 0 && (
            <Tooltip title={`${affaire.taches_terminees} tâche${affaire.taches_terminees !== 1 ? 's' : ''} terminée${affaire.taches_terminees !== 1 ? 's' : ''} sur ${affaire.taches_total}`}>
              <span className={`${styles.tachesTag} ${affaire.taches_terminees === affaire.taches_total ? styles.tachesTagDone : ''}`}>
                {affaire.taches_terminees}/{affaire.taches_total}
              </span>
            </Tooltip>
          )}
        </div>
      </div>

      <div className={styles.meta}>
        {affaire.date_limite && (
          <Tooltip title="Date limite">
            <span className={`${styles.date} ${affaire.retard ? styles.dateRetard : ''}`}>
              <CalendarOutlined style={{ marginRight: 3 }} />
              <Moment format="DD/MM/YY">{affaire.date_limite}</Moment>
            </span>
          </Tooltip>
        )}
        <InlineEditableInput
          canEdit={canManage}
          clickToEdit
          type="select"
          fieldName="responsable_id"
          value={affaire.responsable_id}
          record={affaire}
          onChange={handleFieldChange}
          optionFilterProp="full_name"
          options={users.map(u => ({
            value: u.id,
            label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Avatar size={20} src={u.avatar}>{u.name?.[0]}</Avatar>
                {u.name}
              </span>
            ),
            full_name: u.name,
          }))}
        >
          {affaire.responsable && (
            <Tooltip title={affaire.responsable.name}>
              <Avatar size={24} src={affaire.responsable.avatar} style={{ flexShrink: 0 }}>
                {affaire.responsable.name?.[0]}
              </Avatar>
            </Tooltip>
          )}
        </InlineEditableInput>
        <Space size={2}>
          {canManage && (
            <Tooltip title="Modifier">
              <Button type="text" size="small" icon={<EditOutlined />} style={{ color: 'var(--primary-color, #3E65AC)' }} onClick={() => setEditVisible(true)} />
            </Tooltip>
          )}
          {canManage && (
            <Tooltip title="Supprimer">
              <Popconfirm title="Supprimer ce dossier ?" okType="danger" okText="Supprimer" cancelText="Annuler" onConfirm={() => affairesState.deleteAffaire(affaire.uid)}>
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      </div>

      {editVisible && (
        <AffaireEditForm
          visible={editVisible}
          uid={affaire.uid}
          onUpdate={(updated) => {
            setEditVisible(false);
            affairesState.updateAffaireInList(updated);
            onUpdate?.(updated);
          }}
          onCancel={() => setEditVisible(false)}
        />
      )}
    </div>
  );
};

AffaireBacklogItem.propTypes = { affaire: PropTypes.object.isRequired, onItemClick: PropTypes.func, onUpdate: PropTypes.func };
export default AffaireBacklogItem;
